import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sailboat, IdCard, CalendarDays, Store, ArrowRight, Users, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate } from "@/lib/api";
import { NewsCard } from "@/components/NewsCard";
import { WelcomeHero } from "@/components/WelcomeHero";
import { HomeComunicazioni } from "@/components/HomeComunicazioni";


const Tile = ({ to, icon: Icon, label, value, hint, testid, delay }) => (
  <Link to={to} data-testid={testid} style={{ animationDelay: `${delay}ms` }}
    className="fade-up group bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
    <div className="flex items-center justify-between">
      <Icon className="h-6 w-6 text-emerald-700" />
      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
    </div>
    <div className="text-xs font-mono-x uppercase tracking-wider text-muted-foreground mt-6">{label}</div>
    <div className="font-heading text-3xl font-bold text-[#0B192C] mt-1">{value}</div>
    {hint && <div className="text-sm text-muted-foreground mt-1">{hint}</div>}
  </Link>
);

const AdminStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
  }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="admin-stats">
      <Tile to="/admin" icon={Users} label={`Soci ${s.anno}`} value={s.soci} testid="stat-soci" delay={0} />
      <Tile to="/admin" icon={AlertTriangle} label="Pagamenti da confermare" value={s.da_confermare} testid="stat-da-confermare" delay={60} />
      <Tile to="/admin?tab=tesserati" icon={IdCard} label="Utenti registrati" value={s.utenti} testid="stat-utenti" delay={120} />
    </div>
  );
};

export default function Home() {
  const { user } = useAuth();
  const [ms, setMs] = useState(null);
  const [boats, setBoats] = useState({ mine: [], crew: [] });
  const [regate, setRegate] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/memberships/me").then((r) => setMs(r.data));
    api.get("/notizie", { params: { limit: 3 } }).then((r) => setNews(r.data)).catch(() => setNews([]));
    api.get("/boats").then(async (r) => {
      setBoats(r.data);
      const cats = [...new Set([...r.data.mine, ...r.data.crew].map((b) => b.categoria))];
      const { data } = await api.get("/regate", { params: { upcoming: true, categorie: cats.join(",") } });
      setRegate(data.slice(0, 3));
    });
  }, []);

  const member = ms?.current;
  return (
    <div>
      <WelcomeHero user={user} ms={ms} />

      {user.role === "admin" && <AdminStats />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile to="/tessera" icon={IdCard} label="Tessera" value={member ? member.tessera_numero : "—"} hint={member ? (member.pagamento_confermato ? "Pagamento confermato" : "Pagamento in verifica") : "Non iscritto"} testid="home-tile-tessera" delay={0} />
        <Tile to="/barche" icon={Sailboat} label="Le mie barche" value={boats.mine.length} hint={boats.crew.length ? `Equipaggio su ${boats.crew.length}` : "Gestisci la flotta"} testid="home-tile-barche" delay={60} />
        <Tile to="/regate" icon={CalendarDays} label="Prossima regata" value={regate[0] ? fmtDate(regate[0].data_inizio) : "—"} hint={regate[0]?.luogo || "Nessuna in programma"} testid="home-tile-regate" delay={120} />
        <Tile to="/mercatino" icon={Store} label="Mercatino" value="Annunci" hint="Compra e vendi tra soci" testid="home-tile-mercatino" delay={180} />
      </div>

      {member && <HomeComunicazioni />}

      {news.length > 0 && (
        <section className="mt-12" data-testid="home-news">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-heading text-3xl uppercase text-[#0B192C]">Ultime news</h2>
            <div className="flex gap-4">
              {member && <Link to="/comunicazioni" className="text-sm text-emerald-700 hover:underline" data-testid="home-comunicazioni-link">Comunicazioni della segreteria</Link>}
              <Link to="/notizie" className="text-sm text-emerald-700 hover:underline" data-testid="home-news-all-link">Tutte le news</Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((n, i) => <NewsCard key={n.id} n={n} delay={i * 60} />)}
          </div>
        </section>
      )}
    </div>
  );
}
